import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import React, { forwardRef } from 'react'
import { cn } from '../lib/utils'

export const SelectGroup = SelectPrimitive.Group
export const SelectValue = SelectPrimitive.Value

export const SelectTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
            'flex h-10 w-full items-center justify-between whitespace-nowrap rounded-lg border border-zinc-950/10 bg-transparent px-3.5 py-2.5 text-base/6 text-zinc-950 shadow-sm ring-offset-background placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-zinc-500 dark:border-white/10 dark:text-white sm:py-1.5 sm:text-sm/6 [&>span]:line-clamp-1',
            className
        )}
        {...props}
    >
        {children}
        <SelectPrimitive.Icon asChild>
            <ChevronDown className="size-4 shrink-0 text-zinc-500 dark:text-zinc-400" />
        </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

export const SelectContent = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
    <SelectPrimitive.Portal>
        <SelectPrimitive.Content
            ref={ref}
            position={position}
            className={cn(
                'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-xl bg-white/95 text-zinc-950 shadow-lg ring-1 ring-zinc-950/10 backdrop-blur-xl data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 dark:bg-zinc-800/95 dark:text-white dark:ring-white/10',
                position === 'popper' &&
                'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
                className
            )}
            {...props}
        >
            <SelectPrimitive.Viewport
                className={cn(
                    'p-1',
                    position === 'popper' &&
                    'w-full min-w-[var(--radix-select-trigger-width)]'
                )}
            >
                {children}
            </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

export const SelectLabel = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Label ref={ref} className={cn('px-3 py-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400', className)} {...props} />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

export const SelectItem = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
        ref={ref}
        className={cn(
            'relative flex w-full cursor-default select-none items-center rounded-lg py-2 pl-8 pr-2 text-base/6 outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-primary data-[highlighted]:text-primary-foreground data-[disabled]:opacity-50 sm:text-sm/6',
            className
        )}
        {...props}
    >
        <span className="absolute left-2 flex size-4 items-center justify-center">
            <SelectPrimitive.ItemIndicator>
                <Check className="size-4" />
            </SelectPrimitive.ItemIndicator>
        </span>
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

export const SelectSeparator = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-zinc-950/10 dark:bg-white/10', className)} {...props} />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

// ---------------------------------------------------------------------------
// Compatibility layer
//
// Every existing call site in this app uses the old native-<select>-shaped API:
// `<Select value={x} onChange={(e) => setX(e.target.value)}>` with `<option>`
// children. Radix's Select has a structurally different API (`onValueChange`,
// `Trigger`/`Content`/`Item` composition, no native <select> underneath).
//
// Rather than rewrite every call site as part of the shared-component rebuild,
// `Select` below accepts the legacy prop shape and renders it through the real
// Radix primitives above (a custom popover listbox, not a native picker) — so
// the visual/interaction redesign lands everywhere immediately, and call sites
// only need to move to the composed Select*/onValueChange API when they need
// something the legacy shape can't express (e.g. custom item rendering).
// Note: unlike a native <select>, this does not support `multiple`.
// ---------------------------------------------------------------------------

interface LegacyOption {
    value: string
    label: React.ReactNode
    disabled?: boolean
}

function extractOptions(children: React.ReactNode): LegacyOption[] {
    const options: LegacyOption[] = []
    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return
        if (child.type === 'option') {
            const { value, children: label, disabled } = child.props as {
                value?: string | number
                children?: React.ReactNode
                disabled?: boolean
            }
            options.push({ value: value === undefined ? '' : String(value), label, disabled })
        } else if (child.props && (child.props as any).children) {
            // Fragments/conditional groups wrapping <option> elements
            options.push(...extractOptions((child.props as any).children))
        }
    })
    return options
}

export interface SelectProps
    extends Omit<React.ComponentPropsWithoutRef<typeof SelectTrigger>, 'onChange' | 'value' | 'defaultValue' | 'children'> {
    value?: string | number | null
    defaultValue?: string | number
    name?: string
    required?: boolean
    placeholder?: React.ReactNode
    children?: React.ReactNode
    /** Legacy native-<select>-style change handler. Intentionally `any`-typed as a compatibility bridge so handlers typed against `ChangeEvent<HTMLSelectElement>` elsewhere keep compiling. */
    onChange?: (event: any) => void
    onValueChange?: (value: string) => void
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
    { value, defaultValue, name, required, onChange, onValueChange, children, placeholder, disabled, className, ...props },
    ref
) {
    const options = extractOptions(children)
    const selectableOptions = options.filter((option) => option.value !== '')
    const placeholderOption = options.find((option) => option.value === '')

    const stringValue = value === undefined || value === null ? undefined : String(value)
    const stringDefaultValue = defaultValue === undefined ? undefined : String(defaultValue)

    return (
        <SelectPrimitive.Root
            value={stringValue}
            defaultValue={stringDefaultValue}
            disabled={disabled}
            required={required}
            name={name}
            onValueChange={(next) => {
                onValueChange?.(next)
                onChange?.({ target: { name, value: next } })
            }}
        >
            <SelectTrigger ref={ref} className={className} {...props}>
                <SelectValue placeholder={placeholder ?? placeholderOption?.label} />
            </SelectTrigger>
            <SelectContent>
                {selectableOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                        {option.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </SelectPrimitive.Root>
    )
})
